import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BarChart3, CalendarDays, LineChart, Megaphone, Sparkles } from "lucide-react";
import i18n from "@/lib/i18next";

export const Route = createFileRoute("/for-venues")({
  head: () => ({
    meta: [
      { title: i18n.t("forVenues.metaTitle", { ns: "vendor" }) },
      {
        name: "description",
        content: i18n.t("forVenues.metaDescription", { ns: "vendor" }),
      },
    ],
  }),
  component: ForVenuesPage,
});

const FEATURE_ICONS = [CalendarDays, BarChart3, Megaphone, LineChart] as const;

function ForVenuesPage() {
  const { t } = useTranslation("vendor");

  return (
    <div className="min-h-screen">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-glow" />

      <div className="relative mx-auto max-w-3xl px-5 pb-20 pt-16">
        {/* Hero */}
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-pulse/30 bg-pulse/10 px-3 py-1 text-xs font-semibold text-pulse">
            <Sparkles className="h-3.5 w-3.5" />
            {t("forVenues.badge")}
          </span>
          <h1 className="mt-4 text-4xl font-bold leading-tight sm:text-5xl">
            {t("forVenues.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            {t("forVenues.subtitle")}
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/vendor/register-venue"
              className="inline-flex w-full items-center justify-center rounded-full bg-gradient-pulse px-7 py-3.5 text-sm font-bold text-pulse-foreground shadow-pulse transition-transform active:scale-95 sm:w-auto"
            >
              {t("forVenues.registerCta")}
            </Link>
            <Link
              to="/vendor/login"
              className="inline-flex w-full items-center justify-center rounded-full border border-border bg-surface px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-surface-elevated sm:w-auto"
            >
              {t("forVenues.loginCta")}
            </Link>
          </div>
        </div>

        {/* Feature cards */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {FEATURE_ICONS.map((Icon, i) => {
            const n = i + 1;
            return (
              <div
                key={n}
                className="rounded-2xl border border-border bg-surface-elevated p-5"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-pulse/15 text-pulse">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{t(`forVenues.feature${n}Title`)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {t(`forVenues.feature${n}Desc`)}
                </p>
              </div>
            );
          })}
        </div>

        {/* Closing CTA */}
        <div className="mt-14 rounded-3xl border border-pulse/25 bg-pulse/5 p-8 text-center">
          <h2 className="text-2xl font-bold">{t("forVenues.ctaTitle")}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
            {t("forVenues.ctaSubtitle")}
          </p>
          <Link
            to="/vendor/register-venue"
            className="mt-6 inline-flex items-center justify-center rounded-full bg-gradient-pulse px-7 py-3.5 text-sm font-bold text-pulse-foreground shadow-pulse transition-transform active:scale-95"
          >
            {t("forVenues.ctaButton")}
          </Link>
        </div>
      </div>
    </div>
  );
}
